import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, InputAdornment, LinearProgress, Autocomplete
} from '@mui/material';
import {
  MenuBook, Add, Edit, Delete, Search, Refresh,
  AssignmentReturn, BookmarkAdd, Warning, CheckCircle,
  BarChart, QrCode, Settings as SettingsIcon, FileDownload,
  PersonPin, Report
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const RESOURCE_TYPES = [
  { value: 'book', label: 'Book' },
  { value: 'magazine', label: 'Magazine / Journal' },
  { value: 'digital', label: 'Digital Resource' },
];
const CONDITIONS = ['new','good','fair','poor','damaged'];
const STATUS_CFG = {
  active:   { color: 'success', label: 'On Loan' },
  overdue:  { color: 'error',   label: 'Overdue' },
  returned: { color: 'default', label: 'Returned' },
  lost:     { color: 'error',   label: 'Lost' },
};
const fmt  = n => `E${parseFloat(n||0).toFixed(2)}`;
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/library/stats')
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  if (!stats) return null;
  const { books, loans, top_borrowed, overdue_loans } = stats;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Titles',   value: books?.total_books  || 0, color: '#0f2b4a' },
          { label: 'Total Copies',   value: books?.total_copies || 0, color: '#1565c0' },
          { label: 'Available Now',  value: books?.available    || 0, color: '#2e7d32' },
          { label: 'Currently On Loan', value: books?.on_loan   || 0, color: '#e65100' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">{s.label}</Typography>
                <Typography variant="h4" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Active Loans', value: loans?.active  || 0, color: 'success' },
          { label: 'Overdue',      value: loans?.overdue || 0, color: 'error' },
          { label: 'Lost Books',   value: loans?.lost    || 0, color: 'error' },
          { label: 'Unpaid Fines', value: fmt(loans?.unpaid_fines), color: 'warning' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
              <Chip label={s.value} color={s.color} sx={{ fontWeight: 800, fontSize: 16, px: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>📚 Most Borrowed Books</Typography>
              {!top_borrowed?.length && <Typography fontSize={12} color="text.secondary">No borrowing history yet</Typography>}
              {top_borrowed?.map((b, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={600}>{b.title}</Typography>
                    <Typography fontSize={10} color="text.secondary">{b.accession_number}</Typography>
                  </Box>
                  <Chip label={`${b.times_borrowed}×`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, border: overdue_loans?.length ? '1px solid #fca5a5' : undefined }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom color={overdue_loans?.length ? 'error.main' : 'text.primary'}>
                ⚠️ Overdue Books ({overdue_loans?.length || 0})
              </Typography>
              {!overdue_loans?.length && <Typography fontSize={12} color="success.main">No overdue books — great!</Typography>}
              {overdue_loans?.map((l, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Box>
                    <Typography fontSize={12} fontWeight={600}>{l.borrower_name}</Typography>
                    <Typography fontSize={11} color="text.secondary">{l.title}</Typography>
                    <Typography fontSize={10} color="error.main">Due: {fmtD(l.due_date)}</Typography>
                  </Box>
                  {l.fine_amount > 0 && <Chip label={fmt(l.fine_amount)} size="small" color="error" sx={{ fontSize: 10 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Catalogue Tab ──────────────────────────────────────────────────────────────
function CatalogueTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [books, setBooks]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [filterType, setType]   = useState('');
  const [availOnly, setAvail]   = useState(false);
  const [dialog, setDialog]     = useState(false);
  const [editing, setEditing]   = useState(null);
  const BLANK = { accession_number:'', title:'', author:'', isbn:'', publisher:'',
    publication_year:'', subject_category:'', resource_type:'book',
    total_copies:1, shelf_location:'', description:'', condition:'good' };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/library/books', { params: { search: search || undefined, resource_type: filterType || undefined, available_only: availOnly ? 'true' : undefined } });
      if (r.data.success) setBooks(r.data.data);
    } catch { enqueueSnackbar('Failed to load books', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [search, filterType, availOnly, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setDialog(true); };
  const openEdit = b => { setEditing(b); setForm({ ...b }); setDialog(true); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/library/books/${editing.id}`, form); enqueueSnackbar('Book updated', { variant: 'success' }); }
      else { await api.post('/library/books', form); enqueueSnackbar('Book added to catalogue', { variant: 'success' }); }
      setDialog(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this book from the catalogue?')) return;
    try { await api.delete(`/library/books/${id}`); enqueueSnackbar('Removed', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search title, author, ISBN, accession…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Type" value={filterType} onChange={e => setType(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">All types</MenuItem>
          {RESOURCE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <Button variant={availOnly ? 'contained' : 'outlined'} size="small" onClick={() => setAvail(v => !v)}>
          {availOnly ? '✓ Available Only' : 'Available Only'}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" onClick={() => exportCSV('library_catalogue',
          ['Accession', 'Title', 'Author', 'ISBN', 'Type', 'Category', 'Total', 'Available', 'Shelf', 'Condition'],
          books.map(b => [b.accession_number, b.title, b.author||'', b.isbn||'', b.resource_type, b.subject_category||'', b.total_copies, b.available_copies, b.shelf_location||'', b.condition])
        )} startIcon={<FileDownload />}>Export</Button>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#0f2b4a' }}>Add Book</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Accession #', 'Title', 'Author', 'Category', 'Type', 'Copies', 'Available', 'Shelf', 'Condition', ''].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {books.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No books in catalogue. Click "Add Book" to start.</TableCell></TableRow>}
                {books.map((b, i) => (
                  <TableRow key={b.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>{b.accession_number}</TableCell>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{b.title}</Typography>
                      {b.isbn && <Typography fontSize={10} color="text.secondary">ISBN: {b.isbn}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{b.author || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{b.subject_category || '—'}</TableCell>
                    <TableCell><Chip label={RESOURCE_TYPES.find(t => t.value === b.resource_type)?.label || b.resource_type} size="small" sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{b.total_copies}</TableCell>
                    <TableCell align="center">
                      <Chip label={b.available_copies} size="small"
                        color={b.available_copies > 0 ? 'success' : 'error'} sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{b.shelf_location || '—'}</TableCell>
                    <TableCell><Chip label={b.condition} size="small" color={b.condition === 'good' || b.condition === 'new' ? 'success' : b.condition === 'fair' ? 'warning' : 'error'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(b)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => del(b.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Book Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Book' : 'Add Book to Catalogue'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Accession / Barcode *" value={form.accession_number}
                onChange={e => setForm(f => ({ ...f, accession_number: e.target.value }))}
                disabled={!!editing}
                InputProps={{ startAdornment: <InputAdornment position="start"><QrCode fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Resource Type" value={form.resource_type} onChange={e => setForm(f => ({ ...f, resource_type: e.target.value }))}>
                {RESOURCE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Author" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="ISBN" value={form.isbn} onChange={e => setForm(f => ({ ...f, isbn: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Publication Year" type="number" value={form.publication_year} onChange={e => setForm(f => ({ ...f, publication_year: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Publisher" value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Subject Category" value={form.subject_category} onChange={e => setForm(f => ({ ...f, subject_category: e.target.value }))} placeholder="e.g. Mathematics" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Shelf Location" value={form.shelf_location} onChange={e => setForm(f => ({ ...f, shelf_location: e.target.value }))} placeholder="e.g. A1, B3" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Number of Copies" value={form.total_copies} onChange={e => setForm(f => ({ ...f, total_copies: parseInt(e.target.value) || 1 }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Condition" value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}>
                {CONDITIONS.map(c => <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description / Notes" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Add to Catalogue'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Loans Tab ──────────────────────────────────────────────────────────────────
function LoansTab({ students, teachers }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loans, setLoans]         = useState([]);
  const [books, setBooks]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterStatus, setStatus] = useState('');
  const [filterType, setType]     = useState('');
  const [issueDialog, setIssue]   = useState(false);
  const [returnDialog, setReturn] = useState(null);
  const [lostDialog, setLost]     = useState(null);
  const [settings, setSettings]   = useState({});
  const [form, setForm]           = useState({ book_id: null, borrower_type: 'student', student_id: '', teacher_id: '', staff_name: '', issued_date: new Date().toISOString().split('T')[0], due_date: '', notes: '' });
  const [returnForm, setReturnForm] = useState({ returned_date: new Date().toISOString().split('T')[0], condition: 'good', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/library/loans', { params: { status: filterStatus || undefined, borrower_type: filterType || undefined } });
      if (r.data.success) setLoans(r.data.data);
    } catch { enqueueSnackbar('Failed to load loans', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/library/books').then(r => { if (r.data.success) setBooks(r.data.data); });
    api.get('/library/settings').then(r => { if (r.data.success) setSettings(r.data.data); });
  }, []);

  const openIssue = () => {
    const due = new Date();
    due.setDate(due.getDate() + (settings.max_loan_days || 14));
    setForm(f => ({ ...f, book_id: null, student_id: '', teacher_id: '', notes: '', due_date: due.toISOString().split('T')[0] }));
    setIssue(true);
  };

  const issueLoan = async () => {
    try {
      await api.post('/library/loans', { ...form, book_id: form.book_id?.id });
      enqueueSnackbar('Book issued successfully', { variant: 'success' });
      setIssue(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error issuing book', { variant: 'error' }); }
  };

  const returnBook = async () => {
    try {
      const r = await api.put(`/library/loans/${returnDialog.id}/return`, returnForm);
      enqueueSnackbar(r.data.message, { variant: r.data.days_overdue > 0 ? 'warning' : 'success' });
      setReturn(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const markLost = async () => {
    try {
      await api.put(`/library/loans/${lostDialog.id}/lost`, { notes: lostDialog.notes });
      enqueueSnackbar('Marked as lost. Consider issuing a lost book fee.', { variant: 'warning' });
      setLost(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Borrower" value={filterType} onChange={e => setType(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="student">Students</MenuItem>
          <MenuItem value="teacher">Teachers</MenuItem>
        </TextField>
        <Chip label={`${loans.filter(l => l.status === 'overdue').length} overdue`} color="error" size="small" />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
        <Button size="small" variant="outlined" onClick={() => exportCSV('library_loans',
          ['Book', 'Accession', 'Borrower', 'Type', 'Issued', 'Due', 'Returned', 'Status', 'Fine'],
          loans.map(l => [l.title, l.accession_number,
            l.borrower_type === 'student' ? `${l.s_last} ${l.s_first}` : `${l.t_last} ${l.t_first}`,
            l.borrower_type, fmtD(l.issued_date), fmtD(l.due_date), fmtD(l.returned_date),
            l.status, l.fine_amount > 0 ? fmt(l.fine_amount) : ''])
        )} startIcon={<FileDownload />}>Export</Button>
        <Button variant="contained" startIcon={<Add />} onClick={openIssue} sx={{ bgcolor: '#0f2b4a' }}>Issue Book</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Book', 'Borrower', 'Issued', 'Due Date', 'Returned', 'Status', 'Fine', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No loans found</TableCell></TableRow>}
                {loans.map((l, i) => {
                  const cfg = STATUS_CFG[l.status] || STATUS_CFG.active;
                  const borrowerName = l.borrower_type === 'student'
                    ? `${l.s_last}, ${l.s_first}`
                    : l.borrower_type === 'teacher'
                      ? `${l.t_last}, ${l.t_first}`
                      : (l.staff_name || 'Staff');
                  return (
                    <TableRow key={l.id} hover sx={{ bgcolor: l.status === 'overdue' ? '#fff3f3' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{l.title}</Typography>
                        <Typography fontSize={10} color="text.secondary" fontFamily="monospace">{l.accession_number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PersonPin sx={{ fontSize: 14, color: l.borrower_type === 'student' ? 'primary.main' : 'secondary.main' }} />
                          <Typography fontSize={12}>{borrowerName}</Typography>
                        </Stack>
                        {l.admission_number && <Typography fontSize={10} color="text.secondary">{l.admission_number}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{fmtD(l.issued_date)}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: l.status === 'overdue' ? 'error.main' : 'inherit', fontWeight: l.status === 'overdue' ? 700 : 400 }}>
                        {fmtD(l.due_date)}
                        {l.status === 'overdue' && <Typography fontSize={9} color="error.main">OVERDUE</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtD(l.returned_date)}</TableCell>
                      <TableCell><Chip label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 10, fontWeight: 700 }} /></TableCell>
                      <TableCell>{l.fine_amount > 0 && <Chip label={fmt(l.fine_amount)} size="small" color={l.fine_paid ? 'default' : 'error'} sx={{ fontSize: 10 }} />}</TableCell>
                      <TableCell>
                        {l.status !== 'returned' && l.status !== 'lost' && (
                          <Stack direction="row" spacing={0.3}>
                            <Tooltip title="Return book">
                              <IconButton size="small" color="success" onClick={() => { setReturn(l); setReturnForm({ returned_date: new Date().toISOString().split('T')[0], condition: 'good', notes: '' }); }}>
                                <AssignmentReturn fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as lost">
                              <IconButton size="small" color="error" onClick={() => setLost({ ...l, notes: '' })}>
                                <Report fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Issue Book Dialog */}
      <Dialog open={issueDialog} onClose={() => setIssue(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>Issue Book</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={books.filter(b => b.available_copies > 0)}
                getOptionLabel={b => `${b.title} (${b.accession_number}) — ${b.available_copies} available`}
                value={form.book_id} onChange={(_, v) => setForm(f => ({ ...f, book_id: v }))}
                renderInput={p => <TextField {...p} label="Select Book *" />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Borrower Type" value={form.borrower_type} onChange={e => setForm(f => ({ ...f, borrower_type: e.target.value, student_id: '', teacher_id: '' }))}>
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="teacher">Teacher</MenuItem>
                <MenuItem value="staff">Staff Member</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              {form.borrower_type === 'student' ? (
                <Autocomplete size="small" options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Select Student *" />} />
              ) : form.borrower_type === 'teacher' ? (
                <Autocomplete size="small" options={teachers}
                  getOptionLabel={t => `${t.first_name} ${t.last_name}`}
                  onChange={(_, v) => setForm(f => ({ ...f, teacher_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Select Teacher *" />} />
              ) : (
                <TextField fullWidth size="small" label="Staff Member Name *" value={form.staff_name || ''}
                  onChange={e => setForm(f => ({ ...f, staff_name: e.target.value }))}
                  placeholder="Enter full name of staff member" />
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Issue Date" value={form.issued_date}
                onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Due Date *" value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssue(false)}>Cancel</Button>
          <Button variant="contained" onClick={issueLoan} sx={{ bgcolor: '#0f2b4a' }}>Issue Book</Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={!!returnDialog} onClose={() => setReturn(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Return Book</DialogTitle>
        <DialogContent dividers>
          {returnDialog && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 1 }}>
              <Typography fontWeight={600} fontSize={14}>{returnDialog.title}</Typography>
              <Typography fontSize={12} color="text.secondary">Due: {fmtD(returnDialog.due_date)}</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Return Date" value={returnForm.returned_date}
                onChange={e => setReturnForm(f => ({ ...f, returned_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Book Condition on Return" value={returnForm.condition}
                onChange={e => setReturnForm(f => ({ ...f, condition: e.target.value }))}>
                {CONDITIONS.map(c => <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={returnForm.notes} onChange={e => setReturnForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturn(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={returnBook} startIcon={<AssignmentReturn />}>Confirm Return</Button>
        </DialogActions>
      </Dialog>

      {/* Lost Dialog */}
      <Dialog open={!!lostDialog} onClose={() => setLost(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Lost</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>This book will be marked as lost. A lost book fee should be charged to the borrower via Fee Management.</Alert>
          {lostDialog && <Typography fontWeight={600} sx={{ mb: 2 }}>{lostDialog.title}</Typography>}
          <TextField fullWidth size="small" label="Notes" value={lostDialog?.notes || ''}
            onChange={e => setLost(l => ({ ...l, notes: e.target.value }))} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLost(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={markLost} startIcon={<Report />}>Mark as Lost</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Reservations Tab ───────────────────────────────────────────────────────────
function ReservationsTab({ students, teachers }) {
  const { enqueueSnackbar } = useSnackbar();
  const [reservations, setRes] = useState([]);
  const [books, setBooks]      = useState([]);
  const [loading, setLoading]  = useState(false);
  const [dialog, setDialog]    = useState(false);
  const [form, setForm]        = useState({ book_id: null, borrower_type: 'student', student_id: '', teacher_id: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/library/reservations', { params: { status: 'pending' } });
      if (r.data.success) setRes(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/library/books').then(r => { if (r.data.success) setBooks(r.data.data); }); }, []);

  const save = async () => {
    try {
      await api.post('/library/reservations', { ...form, book_id: form.book_id?.id });
      enqueueSnackbar('Reservation placed (expires in 7 days)', { variant: 'success' });
      setDialog(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const cancel = async (id) => {
    try { await api.put(`/library/reservations/${id}/cancel`); enqueueSnackbar('Cancelled', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Chip label={`${reservations.length} pending reservation(s)`} size="small" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<BookmarkAdd />} onClick={() => setDialog(true)} sx={{ bgcolor: '#0f2b4a' }}>
          Reserve Book
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : reservations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <BookmarkAdd sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No pending reservations</Typography>
        </Paper>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                  {['Book', 'Reserved By', 'Date Reserved', 'Expires', 'Available Copies', ''].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((r, i) => {
                  const borrower = r.borrower_type === 'student'
                    ? `${r.s_last}, ${r.s_first}`
                    : r.borrower_type === 'teacher'
                      ? `${r.t_last}, ${r.t_first}`
                      : (r.staff_name || 'Staff');
                  return (
                    <TableRow key={r.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell><Typography fontSize={12} fontWeight={600}>{r.title}</Typography><Typography fontSize={10} color="text.secondary">{r.accession_number}</Typography></TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{borrower}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtD(r.reserved_date)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtD(r.expiry_date)}</TableCell>
                      <TableCell><Chip label={r.available_copies} size="small" color={r.available_copies > 0 ? 'success' : 'warning'} /></TableCell>
                      <TableCell><Button size="small" color="error" onClick={() => cancel(r.id)}>Cancel</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reserve / Hold a Book</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={books}
                getOptionLabel={b => `${b.title} (${b.accession_number}) — ${b.available_copies} available`}
                onChange={(_, v) => setForm(f => ({ ...f, book_id: v }))}
                renderInput={p => <TextField {...p} label="Select Book *" />} />
            </Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Borrower Type" value={form.borrower_type} onChange={e => setForm(f => ({ ...f, borrower_type: e.target.value }))}>
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="teacher">Teacher</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={8}>
              {form.borrower_type === 'student' ? (
                <Autocomplete size="small" options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Select Student *" />} />
              ) : (
                <Autocomplete size="small" options={teachers}
                  getOptionLabel={t => `${t.first_name} ${t.last_name}`}
                  onChange={(_, v) => setForm(f => ({ ...f, teacher_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Select Teacher *" />} />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ fontSize: 12 }}>Reservation expires after 7 days if not collected.</Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>Place Reservation</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Library() {
  const [tab, setTab]           = useState(0);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    import('../services/api').then(({ studentsAPI, teachersAPI }) => {
      studentsAPI.getAll({ limit: 2000 }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
      teachersAPI.getAll({ limit: 500 }).then(r => { if (r.data.success) setTeachers(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBook color="primary" /> Library Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Book catalogue · Lending · Reservations · Overdue tracking · Fines
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />} label="Overview" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<MenuBook fontSize="small" />} label="Catalogue" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<AssignmentReturn fontSize="small" />} label="Loans" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<BookmarkAdd fontSize="small" />} label="Reservations" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab /></TabPanel>
      <TabPanel value={tab} index={1}><CatalogueTab /></TabPanel>
      <TabPanel value={tab} index={2}><LoansTab students={students} teachers={teachers} /></TabPanel>
      <TabPanel value={tab} index={3}><ReservationsTab students={students} teachers={teachers} /></TabPanel>
    </Box>
  );
}
